import { generatePagerProps } from '@fukuri-admins/utils/common/generatePagerProps';
import { DeleteClientDialog } from './features/DeleteClientDialog';
import { useClientList } from './hooks';
import { convertToPresentationProps } from './logics';
import { ClientListPresentation } from './presentations';
import { ClientListEmptyPresentation } from './presentations/empty';

/**
 * 利用企業一覧 Container
 *
 * - 状態管理・アクションは useClientList に委譲する
 * - API 型 → Presentation 型の変換は convertToPresentationProps に委譲する
 * - Empty / 通常表示の出し分けは isEmpty フラグで行う（三項演算子）
 * - 検索条件あり × 0 件のときは Empty を表示せず検索フォーム＋テーブルを表示する（PEN-309 対応）
 * - カスタムフィールド定義に基づいてテーブル列を動的に挿入する（PEN-563 T04）
 * - creditEvaluationEnabled=true のとき与信状況列・未与信件数アラート・与信確認モーダルを表示する（PEN-564 T05）
 * - 利用企業削除ダイアログを Overlay パターンで並列配置する（PEN-566 T07a）
 * - T08（PEN-699）: 削除成功後の一覧再フェッチ・未与信カウント再取得
 * - threeLetterCode は DeleteClientDialog Feature 内部で Cookie から取得するため、ここでは渡さない
 * - 取引店舗は PartnerBranch 由来の固定列として全環境（production / demo 含む）で表示する。
 *   表示可否は hasPartnerBranch（マスタ取引店舗一覧 API に1件以上存在するか）単独で判定し、
 *   ClientsTable の取引店舗列・DeleteClientDialog の取引店舗表示の両方に同じ値を渡す（PEN-989）。
 *   パートナー配下に取引店舗が1件も無い場合は列ごと非表示にする。
 *   raw データ（partnerBranchesData）は useClientList から受け取り、hasPartnerBranch への導出は
 *   convertToPresentationProps の責務とする（計算は converter で行う）
 * - カスタムフィールド「取引店舗」は convertToPresentationProps 内で無条件に除外する（PartnerBranch
 *   固定列との二重表示防止。PEN-988 / PEN-989）
 *
 * NOTE: 親コンポーネント（Screen）で Suspense にラップすること
 * NOTE: PEN-569（利用企業一覧 新パターン刷新）の後続チケットで段階的に拡充する
 */
export const ClientList: React.FC = () => {
  const {
    state: {
      clientsData,
      totalPage,
      currentPage,
      totalCount,
      nameValue,
      creditGrantedValue,
      isFiltered,
      featureFlagsData,
      customFieldDefinitionsData,
      ungrantedCreditCount,
      isDownloadingCsv,
      deleteTargetClient,
      partnerBranchesData,
      tooltipId,
    },
    actions: {
      handlePageChange,
      handleEditClient,
      handleDeleteClient,
      handleCloseDeleteClientDialog,
      handleDeleteClientSuccess,
      handleRowClick,
      handleChangeName,
      handleChangeCreditGranted,
      handleSearch,
      handleResetFilters,
      handleDownloadCsv,
      handleGrantCredit,
      handleShowTooltip,
      handleHideTooltip,
    },
  } = useClientList();

  const { rows, customFieldColumns, isEmpty, creditEvaluationEnabled, hasPartnerBranch } =
    convertToPresentationProps({
      clientsData,
      isFiltered,
      featureFlagsData,
      customFieldDefinitionsData,
      ungrantedCreditCount,
      partnerBranchesData,
    });

  const pagerProps = generatePagerProps({
    currentPage,
    totalPage,
    totalCount,
    rows,
    onPageChange: handlePageChange,
  });

  return (
    <>
      {isEmpty ? (
        <ClientListEmptyPresentation />
      ) : (
        <ClientListPresentation
          rows={rows}
          customFieldColumns={customFieldColumns}
          pagerProps={pagerProps}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onRowClick={handleRowClick}
          nameValue={nameValue}
          onChangeName={handleChangeName}
          creditGrantedValue={creditGrantedValue}
          onChangeCreditGranted={handleChangeCreditGranted}
          creditEvaluationEnabled={creditEvaluationEnabled}
          hasPartnerBranch={hasPartnerBranch}
          ungrantedCreditCount={ungrantedCreditCount}
          onGrantCredit={handleGrantCredit}
          onSearch={handleSearch}
          onReset={handleResetFilters}
          onDownloadCsv={handleDownloadCsv}
          isDownloadingCsv={isDownloadingCsv}
          showTooltip={handleShowTooltip}
          hideTooltip={handleHideTooltip}
          tooltipId={tooltipId}
        />
      )}
      {deleteTargetClient && (
        <DeleteClientDialog
          name={deleteTargetClient.name}
          partnerBranchName={deleteTargetClient.partnerBranchName}
          contactPersonName={deleteTargetClient.contactPersonName}
          contractPlanName={deleteTargetClient.contractPlanName}
          userCount={deleteTargetClient.userCount}
          clientId={String(deleteTargetClient.id)}
          hasPartnerBranch={hasPartnerBranch}
          onDelete={handleDeleteClientSuccess}
          onClose={handleCloseDeleteClientDialog}
        />
      )}
    </>
  );
};
